
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Text,
  Circle,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Flex
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

interface ColorOption {
  label: string;
  value: string;
  displayColor: string;
}

interface ColorSelectProps {
  options: ColorOption[];
  defaultValue?: string;
  onChange: (value: string) => void;
  value?: string;
  placeholder?: string;
  error?: boolean;
  onError?: (hasError: boolean) => void;
  bgColor?: string;
  h?: string;
}

const ColorOption: React.FC<{ color: string, label: string, isSelected?: boolean, onClick?: () => void }> = ({ color, label, isSelected, onClick }) => {
  return (
    <MenuItem
      onClick={onClick}
      bg={isSelected ? "primary.900" : "transparent"}
      _hover={{ bg: "primary.900" }}
      borderRadius="md"
      transition="all 0.2s"
      px={3}
      py={2}
    >
      <Flex align="center" width="100%">
        <Circle 
          size="24px" 
          bg={color} 
          border="1px solid"
          borderColor={color === "#FFFFFF" ? "gray.300" : "transparent"}
          mr={3}
          boxShadow="0 0 0 1px rgba(0,0,0,0.1) inset"
        />
        <Text fontSize="md" color="white" fontWeight={isSelected ? "medium" : "normal"}>
          {label}
        </Text>
        {isSelected && (
          <Box ml="auto" color="primary.900">
            <Box as="svg" viewBox="0 0 24 24" width="18px" height="18px" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </Box>
          </Box>
        )}
      </Flex>
    </MenuItem>
  );
};

const ColorSelect: React.FC<ColorSelectProps> = ({
  options,
  onChange,
  value,
  placeholder,
  error = false,
  onError,
  bgColor = "primary.900",
  h = "42px",
}) => {
  const selectedOption = options.find(option => option.value === value);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuWidth, setMenuWidth] = useState<string | number>('auto');

  useEffect(() => {
    if (buttonRef.current) {
      setMenuWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  const handleSelect = (optionValue: string) => {
    if (onError && optionValue) {
      onError(false);
    }
    onChange(optionValue);
  };

  return (
    <Menu closeOnSelect autoSelect={false}>
      {({ isOpen }) => (
        <>
          <MenuButton
            ref={buttonRef}
            as={Button}
            w="100%"
            h={h}
            borderRadius="md"
            bg={bgColor}
            fontSize="lg"
            _hover={{ bg: "primary.900" }}
            _active={{ bg: "primary.900" }}
            rightIcon={
              <ChevronDownIcon 
                transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                transition="transform 0.2s"
                color="white"
              />
            }
            _focus={{
              outline: "none",
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-900)",
            }}
            borderColor={error ? "red.300" : "#7E7D7D"}
            borderWidth={error ? "2px" : "1px"}
            px={3}
            textAlign="left"
            justifyContent="space-between"
          >
            {selectedOption ? (
              <Flex align="center">
                <Circle 
                  size="20px" 
                  bg={selectedOption.displayColor} 
                  border="1px solid"
                  borderColor={selectedOption.displayColor === "#FFFFFF" ? "primary.900" : "transparent"}
                  mr={2}
                  boxShadow="0 0 0 1px rgba(0,0,0,0.1) inset"
                />
                <Text color="gray.100" fontWeight="normal">{selectedOption.label}</Text>
              </Flex>
            ) : (
              <Text color="gray.100" fontWeight="normal">{placeholder || "Color"}</Text>
            )}
          </MenuButton>
          <MenuList
            bg="primary.800"
            borderColor="primary.900"
            boxShadow="0 4px 12px rgba(0,0,0,0.15)"
            zIndex={1000}
            w={menuWidth}
          >
            <Box maxH="250px" overflowY="auto">
              {options.map((option) => (
                <ColorOption
                  key={option.value}
                  color={option.displayColor}
                  label={option.label}
                  isSelected={option.value === value}
                  onClick={() => handleSelect(option.value)}
                />
              ))}
            </Box>
          </MenuList>
        </>
      )}
    </Menu>
  );
};

export default ColorSelect;